// src/oura/visibility/fromLive.ts
//
// The Visibility model from the blocks the backend already ships: the queries,
// the probes, `aiVisibility`, `grounding`, answer coverage, the Search Console
// blocks and the score history. A fixture business passes live-shaped data
// through this same function.
//
// Three rules the old screen got wrong live here, once:
//
//   * THE CHATGPT APP IS THE VERDICT. The backend stores the app's answers as
//     engine `chatgpt_app` and keeps an `openai` API row per question for its
//     links only (`mention_probes` in aeo_visibility.py). Whenever the app ran,
//     the API rows are never a verdict, or the column would report the
//     instrument that under-counts.
//   * WITHHELD ROWS COUNT FOR NOTHING. A row whose prose was not an answer to
//     the question leaves the numerator and the denominator, as it does on the
//     backend; counted, it would read as "not named".
//   * NO FIGURE WITHOUT A SOURCE. Google's AI has no per-question reading on
//     live, the named-count history is not recorded yet, and the benchmark has
//     no endpoint. Each is left absent, never filled with a zero.

import type {
    AeoAiVisibility,
    AeoAnswerCoverage,
    AeoBusinessCompetitor,
    AeoBusinessDetail,
    AeoCompetitorWin,
    AeoEngineShare,
    AeoGoogleAiPending,
    AeoGoogleAiReading,
    AeoGrounding,
    AeoLevelCap,
    AeoProbe,
    AeoQuery,
    AeoQuestionCorrection,
    AeoVerdictCorrection,
    AeoVerdictEngine,
    AeoScoreHistoryEntry,
    AeoSearchPerformance,
    AeoSearchQuestions,
    AeoTrackerSummaryTask,
} from "@service/aeo";
import type { VisibilityCheckName } from "../audit";
import { ENGINE_LABEL, engineDisplayName, shortDate } from "../lib/oura";
import { instrumentColumnLabel, isPartialInstrument } from "../components/aeo/InstrumentBadge";
import { absenceText, engineIdentity, RENTED_VISIBILITY_CAP_BELOW } from "../places";
import { matchesBySlot, searchEmptyState } from "../topSearches";
import { trendBreakIndex, type TrendBreak } from "../trendBreak";
import {
    countedEngines,
    VIS_ENGINES,
    weakestEngine,
    type EngineFilter,
    type VisCell,
    type VisEngine,
    type VisEngineStat,
    type VisExcerpt,
    type VisModel,
    type VisOpsRow,
    type VisRival,
    type VisRow,
    type VisSources,
    type VisTrend,
    type VisTrendBreakCause,
    type VisGooglePending,
    type VisTrendPoint,
} from "./model";
import type { VisPriorScan } from "./rivalFacts";
import type { ScanQuestions } from "./questionSets";

/** Everything the model is built from, as the payload carries it. */
export interface VisibilityInputs {
    /** The scan's own capture date. */
    capturedAt: string;
    /** The scan on screen, to find its row in the history (cost, trend end). */
    assessmentId: string | null;
    vis: AeoAiVisibility | null;
    /** A correction's live state per question number: a question clearing or asking reads "Re-asking…". */
    questionCorrections?: Record<string, AeoQuestionCorrection>;
    /** Staff verdict corrections per question number, then engine. */
    verdictCorrections?: Record<string, Partial<Record<AeoVerdictEngine, AeoVerdictCorrection>>>;
    queries: AeoQuery[];
    /** Absent, not empty, when the engines never ran. */
    probes: AeoProbe[] | undefined;
    /**
     * Google's AI Overview per question (#3945, ourteam-backend#5123).
     *
     * A separate input because Google is NOT in `probes[]` and never was: the
     * backend stores these rows on their own so they can carry "Google showed
     * no overview" and "the read failed", neither of which a probe can say.
     * Absent, not empty, on a scan from before it — the column then falls back
     * to whatever `probes` held, which is how older scans keep rendering.
     */
    googleAiReadings: AeoGoogleAiReading[] | undefined;
    /** Google's AI pages still being read (ourteam-backend#5619). Absent on a
     *  finished scan, and then the screens render as they always have. */
    googleAiPending?: AeoGoogleAiPending | null;
    grounding: AeoGrounding | null;
    answerCoverage: AeoAnswerCoverage | null | undefined;
    searchQuestions: AeoSearchQuestions | null | undefined;
    searchPerformance: AeoSearchPerformance | null | undefined;
    trendBreak: TrendBreak | null;
    /**
     * `businessAudit.competitors`: every rival the scan found, with its website
     * when one is on record and the scan's own line about it. Rivals the
     * answers never named are on it too, which is why it is merged into the
     * Competitors list rather than only read for domains.
     */
    auditCompetitors: AeoBusinessCompetitor[];
    /** `report_copy.competitors`: the branded report's paragraph per rival. */
    reportCompetitors: { name: string; narrative: string }[];
    history: AeoScoreHistoryEntry[];
    levelCappedBy: AeoLevelCap | null;
    /** A typical business we measure. No endpoint yet; BE handoff "Benchmark". */
    benchmark?: number | null;
    /** A stored picture of the ChatGPT answer for one slot. Fixture only:
     *  live captures are fetched per question by AnswerScreenshot. */
    captureFor?: (slot: number) => { src: string; takenAt: string | null } | null;
    /** The plan's tasks. Fixture only: a live business reads the plan
     *  summary route, which the screen fetches itself. */
    fixTasks?: AeoTrackerSummaryTask[] | null;
    /**
     * The previous scan that asked the same questions, reduced to what the
     * Competitors tab compares against. Fixture only: on a live business the
     * screen fetches that scan itself (history + getAssessment) and builds the
     * same shape through `priorScanFrom`, so both paths feed one comparison.
     */
    prior?: VisPriorScan | null;
    /**
     * Earlier scans' questions and verdicts, by assessment id, for the
     * Question history diff. Fixture only: a live business fetches each scan
     * itself (useScanQuestions) and reduces it to the same shape.
     */
    questionSets?: Record<string, ScanQuestions>;
}

/* ------------------------------------------------------------------------- *
 * Probes
 * ------------------------------------------------------------------------- */

const key = (text: string | null | undefined) => (text ?? "").trim().toLowerCase();

/** Names once each, case-insensitively, in first-seen order. */
function uniqueNames(names: string[]): string[] {
    const seen = new Map<string, string>();
    names.forEach((n) => {
        if (n.trim() && !seen.has(key(n))) seen.set(key(n), n);
    });
    return Array.from(seen.values());
}

/** The merged names first, the same names the Competitors list uses. */
const probeRivals = (p: AeoProbe) => p.rivalNames ?? p.rivals ?? [];

/** The column a probe belongs to, or null for an engine this screen does not report. */
function engineOf(p: AeoProbe): VisEngine | null {
    const engine = (p.engine ?? "").toLowerCase();
    if (p.instrument === "google_serp" || engine === "google_ai") return "ai_overview";
    if (engine === "chatgpt_app" || engine === "openai" || engine === "chatgpt") return "chatgpt";
    if (engine === "gemini" || engine === "google") return "gemini";
    return null;
}

/** A row that produced an answer which may be scored (`answered()` on the backend). */
function answered(p: AeoProbe): boolean {
    return !p.withheld && (p.answer ?? "").trim().length > 0;
}

/**
 * Probes grouped by question, verdict rows only. When the app ran anywhere on
 * the run, every `openai` row is a link row and is left out, exactly as the
 * backend's headline counts leave it out.
 */
function verdictProbesByQuestion(probes: AeoProbe[]): Map<string, AeoProbe[]> {
    const appRan = probes.some((p) => (p.engine ?? "").toLowerCase() === "chatgpt_app");
    const by = new Map<string, AeoProbe[]>();
    for (const p of probes) {
        if (appRan && (p.engine ?? "").toLowerCase() === "openai") continue;
        const k = key(p.query);
        // Unjoinable: skip it rather than guess which question it answered.
        if (!k) continue;
        const list = by.get(k);
        if (list) list.push(p);
        else by.set(k, [p]);
    }
    return by;
}

function cellFor(probes: AeoProbe[]): VisCell | null {
    const counted = probes.filter(answered);
    if (counted.length === 0) return null;
    const hits = counted.filter((p) => p.mentioned).length;
    return { named: hits > 0, hits, samples: counted.length, rivals: uniqueNames(counted.flatMap(probeRivals)) };
}

/* ------------------------------------------------------------------------- *
 * Google's AI, per question
 * ------------------------------------------------------------------------- */

/** The reading for each question, by slot `n`, and by question text for a row
 *  whose slot the readings do not carry. */
export function googleAiByQuestion(readings: AeoGoogleAiReading[]): { bySlot: Map<number, AeoGoogleAiReading>; byKey: Map<string, AeoGoogleAiReading> } {
    const bySlot = new Map<number, AeoGoogleAiReading>();
    const byKey = new Map<string, AeoGoogleAiReading>();
    for (const r of readings) {
        if (typeof r.n === "number" && !bySlot.has(r.n)) bySlot.set(r.n, r);
        const k = key(r.query);
        if (k && !byKey.has(k)) byKey.set(k, r);
    }
    return { bySlot, byKey };
}

/**
 * The reading that belongs to one question: by slot, but only when the slot's
 * own question is that question; otherwise by the question's text.
 *
 * The slot alone was not enough (Jack, #3944). `id` falls back to the row's
 * POSITION when a query carries no `n`, so it can land on a slot the readings
 * hold for a different question — the lookup then succeeds, the text fallback
 * never runs, and the row shows another question's verdict, rivals and
 * overview. Checking the slot's question before trusting it costs one
 * comparison and makes the fallback reachable in the case its comment claims.
 */
export function googleAiFor(
    gai: { bySlot: Map<number, AeoGoogleAiReading>; byKey: Map<string, AeoGoogleAiReading> },
    id: number,
    questionKey: string,
): AeoGoogleAiReading | undefined {
    const bySlot = gai.bySlot.get(id);
    return bySlot && key(bySlot.query) === questionKey ? bySlot : gai.byKey.get(questionKey);
}

/**
 * One question's Google cell.
 *
 * `shown !== true || mentioned !== true` is NOT NAMED — a question Google
 * rendered no overview for, and one whose read failed, are misses like any
 * other (product decision on #3945, 30 Sep). The row still exists, so it is
 * counted in the fifty, which is what makes this column comparable with
 * ChatGPT's and Gemini's.
 *
 * `samples` is 1 so the shared mark never prints "1/1".
 */
function googleAiCell(r: AeoGoogleAiReading): VisCell {
    const named = r.shown === true && r.mentioned === true;
    return {
        named,
        hits: named ? 1 : 0,
        samples: 1,
        // Only an overview Google actually showed can have named a rival.
        rivals: r.shown === true ? uniqueNames(r.rivalNames ?? r.rivals ?? []) : [],
        // `shown: null` is a read that failed, not a page Google left bare (#4053).
        // Only the type grid reads this; every other count still holds it as a miss.
        ...(r.shown === null ? { unread: true } : {}),
    };
}

/** The overview itself, for the drawer. Only a shown overview has one. */
function googleAiExcerpt(r: AeoGoogleAiReading): VisExcerpt | undefined {
    if (r.shown !== true || !(r.answer ?? "").trim()) return undefined;
    return {
        text: r.answer,
        truncated: !!r.answer_truncated,
        instrument: r.instrument ?? null,
        citedPages: r.sources ?? [],
    };
}

function excerptFor(probes: AeoProbe[]): VisExcerpt | undefined {
    const p = probes.find(answered);
    if (!p) return undefined;
    return {
        text: p.answer,
        truncated: !!p.answer_truncated,
        instrument: p.instrument ?? null,
        citedPages: p.cited_pages ?? [],
    };
}

/* ------------------------------------------------------------------------- *
 * Engines
 * ------------------------------------------------------------------------- */

/**
 * What a Google's AI row that was not read has to say. Neither reason is a
 * zero: "Google rendered no overview" is a fact about Google, and "we could
 * not read it" a fact about our read.
 */
/**
 * Google's AI pages still being read. Gated ONLY on `results.googleAiPending`
 * (ourteam-backend#5619): absent means nothing is pending, and the screens
 * render exactly as before, including for a `read_failed` engine row.
 */
export function googlePendingOf(block: AeoGoogleAiPending | null | undefined): VisGooglePending | null {
    if (!block || typeof block.pages !== "number") return null;
    return {
        pages: block.pages,
        of: typeof block.of === "number" ? block.of : null,
        rereading: typeof block.rereading === "boolean" ? block.rereading : null,
        sentence: null,
    };
}

function googleAiSkip(row: AeoEngineShare | undefined): string {
    if (!row) return "Not read on this scan";
    if (row.skipReason === "no_ai_overviews") {
        const asked = row.coverage?.asked;
        return asked
            ? `Google showed no AI Overview for any of the ${asked} questions.`
            : "Google showed no AI Overview for any of the questions.";
    }
    if (row.skipReason === "read_failed") return row.skipDetail?.trim() || "Google's AI could not be read on this scan.";
    return row.summary?.trim() || "Not read on this scan";
}

/**
 * The reason an engine was not asked, as one readable sentence. The backend's
 * detail can be a raw exception; the first sentence carries the fact.
 */
function skipSentence(detail: string | null | undefined): string {
    const raw = (detail ?? "").trim();
    if (!raw) return "Not asked on this scan";
    const first = raw.replace(/^[A-Za-z.]*(Error|Exception):\s*/, "").split(/(?<=[.!?])\s/)[0].trim();
    return first.length > 120 ? `${first.slice(0, 117).trimEnd()}…` : first;
}

/**
 * Why an assistant has no figure. "Not asked" only when the run says it was
 * not asked: an engine that was asked and whose every answer was withheld
 * or empty was asked, and saying otherwise sends staff looking for a
 * configuration fault that is not there.
 */
function assistantSkip(row: AeoEngineShare | undefined, probed: boolean): string {
    if (row?.probed === false) return skipSentence(row.skipDetail || row.summary);
    if (row || probed) return "Asked, but no answer could be scored";
    return skipSentence(undefined);
}

function assistantStat(
    engine: "chatgpt" | "gemini",
    vis: AeoAiVisibility | null,
    rows: VisRow[],
    grounding: AeoGrounding | null,
    /** The run holds verdict probes for this engine, scored or not. */
    probed: boolean,
): VisEngineStat {
    const rates = vis?.engineRates ?? {};
    const rate = engine === "chatgpt" ? rates.chatgpt_app ?? rates.openai ?? rates.chatgpt : rates.gemini;
    const cells = rows.map((r) => r.cells[engine]).filter((c): c is VisCell => c !== null);
    const label = engine === "chatgpt" ? instrumentColumnLabel(grounding, ENGINE_LABEL.chatgpt) : ENGINE_LABEL.gemini;
    const row = (vis?.engines ?? []).find((e) => !e.citationOnly && engineIdentity(e.engine) === engine);
    // The backend's own counts first: they already hold out branded,
    // unwinnable and withheld rows. The probe join stands in only on a run
    // from before engineRates shipped.
    const named = rate ? rate.mentioned : cells.length ? cells.filter((c) => c.named).length : null;
    const of = rate ? rate.answered : cells.length ? cells.length : null;
    return {
        key: engine,
        label,
        named,
        of,
        instrument: engine === "chatgpt" ? grounding?.instrumentLabel ?? null : null,
        skipped: of === null ? assistantSkip(row, probed) : null,
        capturedAt: null,
        coverage: null,
        perQuestion: cells.length > 0,
    };
}

function googleAiStat(vis: AeoAiVisibility | null, rows: VisRow[]): VisEngineStat {
    // By INSTRUMENT: the old placeholder row was also called "Google AI" and
    // carried `instrument: "api"`; only `google_serp` is a read we performed.
    const row = (vis?.engines ?? []).find((e) => e.instrument === "google_serp");
    const read = !!row && row.probed !== false;
    /**
     * COUNTED FROM THE CELLS, like the assistants, and for two reasons.
     *
     * The denominator is the questions asked, not the overviews Google showed
     * (product decision on #3945, 30 Sep): the column is only comparable with
     * ChatGPT's and Gemini's if all three are out of the same fifty. The
     * backend's own `answered` is the shown count — 25 where 50 were asked —
     * so it is the wrong number to take here.
     *
     * And `aiVisibility.engines` is rebuilt from `probes[]`, where Google has
     * never been, so a recompute of that block deletes the Google row while
     * the readings survive (ourteam-backend#5136). Counting the cells means the
     * tile keeps working through that, and agrees with the grid beside it,
     * which counts the same cells.
     */
    const cells = rows.map((r) => r.cells.ai_overview).filter((c): c is VisCell => c !== null);
    // A question whose read FAILED is not measured, so it leaves both sides of "n of m", as it does in the
    // type grid (#4053). A question where Google showed no overview stays in: that is a read, and a miss
    // (#3945). Without this the header and Home said "12 of 50" beside a grid reading "12 of 39" (#4373).
    const readCells = cells.filter((c) => c.unread !== true);
    // Pending pages are no cell, so the total leaves them out too. Nothing read, on either path: no figure,
    // never "0 of 0", so the engine's skip reason shows instead.
    const fallbackMeasured = read && row.coverage
        ? Math.max(0, row.coverage.asked - (row.coverage.pending ?? 0) - (row.coverage.failed ?? 0))
        : null;
    const of = cells.length
        ? (readCells.length > 0 ? readCells.length : null)
        : fallbackMeasured !== null && fallbackMeasured > 0 ? fallbackMeasured : null;
    const named = of === null ? null : cells.length ? readCells.filter((c) => c.named).length : row?.mentions ?? null;
    return {
        key: "ai_overview",
        label: ENGINE_LABEL.ai_overview,
        named,
        of,
        // Read through a SERP provider now, not a browser we drive.
        instrument: of === null ? null : "Google results page",
        skipped: of === null ? googleAiSkip(row) : null,
        capturedAt: row?.capturedAt ?? null,
        coverage: row?.coverage ?? null,
        perQuestion: cells.length > 0,
    };
}

/* ------------------------------------------------------------------------- *
 * Trend
 * ------------------------------------------------------------------------- */

const EMPTY_VALUES: Record<EngineFilter, number | null> = { all: null, chatgpt: null, gemini: null, ai_overview: null };

const CHATGPT_INSTRUMENT: Record<string, string> = { chatgpt_app: "the ChatGPT app", openai: "the OpenAI API" };

/**
 * The newest point at which ChatGPT was asked a different way from the point
 * before (the API against the app), as a break the same rule can place. The
 * app names a business far more often than the API for the same questions,
 * so a line across that change is two instruments subtracted.
 */
function instrumentBreak(recorded: AeoScoreHistoryEntry[]): TrendBreak | null {
    const used = recorded.map((h) => h.visibility?.engines.chatgpt?.instrument ?? null);
    for (let i = used.length - 1; i > 0; i -= 1) {
        const before = used[i - 1];
        const now = used[i];
        if (before && now && before !== now) {
            return {
                at: recorded[i].captured_at,
                reason: `ChatGPT was asked through ${CHATGPT_INSTRUMENT[now] ?? now} from here, ${CHATGPT_INSTRUMENT[before] ?? before} before`,
            };
        }
    }
    return null;
}

/**
 * The newest point measured differently from the one before it, as the points
 * themselves record it: a point stamped with its own trend break (a re-author,
 * a slot re-fill, a model or instrument change), or a question set that is not
 * the one before it. Placed at that point's own date, so the one break rule
 * puts it exactly between the two.
 */
function pointBreak(recorded: AeoScoreHistoryEntry[]): TrendBreak | null {
    for (let i = recorded.length - 1; i > 0; i -= 1) {
        const now = recorded[i].visibility;
        const before = recorded[i - 1].visibility;
        if (now?.trendBreak) return { at: recorded[i].captured_at, reason: now.trendBreak.reason };
        if (now?.questionSetId && before?.questionSetId && now.questionSetId !== before.questionSetId) {
            return { at: recorded[i].captured_at, reason: "the question set changed here" };
        }
    }
    return null;
}

/** What a stamped break changed, from the backend's own reason. */
function breakCause(reason: string): VisTrendBreakCause {
    if (/question set/i.test(reason)) return "questions";
    if (/instrument|probe mode/i.test(reason)) return "instrument";
    return "method";
}

/** The recipe's own break: where the set last changed, when the payload does not say. */
function recipeBreak(recorded: AeoScoreHistoryEntry[]): TrendBreak | null {
    const recipes = recorded.map((h) => h.visibility?.recipe ?? null);
    const current = recipes[recipes.length - 1];
    let first = recipes.length - 1;
    while (first > 0 && recipes[first - 1] === current) first -= 1;
    return current && first > 0 ? { at: recorded[first].captured_at, reason: "the question set changed here" } : null;
}

/**
 * The named-count series when the score history records it, else the live
 * mentions series under its own unit. Points after the scan on screen are
 * dropped, so a past scan's line ends where that scan stood.
 */
export function buildTrend(inputs: VisibilityInputs): VisTrend {
    const upTo = Date.parse(inputs.capturedAt);
    const recorded = inputs.history
        .filter((h) => h.visibility && !h.gated && (Number.isNaN(upTo) || Date.parse(h.captured_at) <= upTo))
        .sort((a, b) => Date.parse(a.captured_at) - Date.parse(b.captured_at));

    if (recorded.length > 0) {
        const points: VisTrendPoint[] = recorded.map((h) => {
            const v = h.visibility;
            return {
                date: h.captured_at,
                values: {
                    all: v?.questionsNamed ?? null,
                    chatgpt: v?.engines.chatgpt?.named ?? null,
                    gemini: v?.engines.gemini?.named ?? null,
                    ai_overview: v?.engines.googleAi?.named ?? null,
                },
                of: {
                    all: v?.questionsOf ?? null,
                    chatgpt: v?.engines.chatgpt?.answered ?? null,
                    gemini: v?.engines.gemini?.answered ?? null,
                    ai_overview: v?.engines.googleAi?.answered ?? null,
                },
            };
        });
        // Every change of method goes through the one break rule: the
        // points' own breaks (a stamped break, a changed question set), and a
        // change of ChatGPT instrument. Points from before the history carried
        // either field fall back to this scan's stamped break, else the
        // recipes' own. The newest wins, because the line only joins points
        // measured the way the latest one was.
        const values = points.map((p) => p.values.all ?? 0);
        const dates = points.map((p) => p.date);
        const stamped = recorded.some((h) => h.visibility?.questionSetId !== undefined || h.visibility?.trendBreak !== undefined);
        const setBreak = stamped ? pointBreak(recorded) : inputs.trendBreak ?? recipeBreak(recorded);
        const toolBreak = instrumentBreak(recorded);
        const candidates: { brk: TrendBreak; cause: VisTrendBreakCause }[] = [
            ...(setBreak ? [{ brk: setBreak, cause: breakCause(setBreak.reason) }] : []),
            ...(toolBreak ? [{ brk: toolBreak, cause: "instrument" as const }] : []),
        ];
        let latest: { index: number; brk: TrendBreak; cause: VisTrendBreakCause } | null = null;
        for (const c of candidates) {
            const index = trendBreakIndex(values, dates, c.brk);
            if (index !== null && (latest === null || index > latest.index)) latest = { index, ...c };
        }
        return {
            unit: "questions",
            points,
            breakIndex: latest?.index ?? null,
            breakReason: latest?.brk.reason ?? null,
            breakCause: latest?.cause ?? null,
        };
    }

    const series = inputs.vis?.trend ?? [];
    const dates = inputs.vis?.trendCapturedAt ?? [];
    const breakIndex = trendBreakIndex(series, dates, inputs.trendBreak);
    return {
        unit: "answers",
        points: series.map((v, i) => ({ date: dates[i] ?? null, values: { ...EMPTY_VALUES, all: v }, of: EMPTY_VALUES })),
        breakIndex,
        breakReason: breakIndex !== null ? inputs.trendBreak?.reason ?? null : null,
        breakCause: breakIndex !== null && inputs.trendBreak ? breakCause(inputs.trendBreak.reason) : null,
    };
}

/* ------------------------------------------------------------------------- *
 * Rivals and sources
 * ------------------------------------------------------------------------- */

const RIVALS_LISTED = 10;

/**
 * A rival the scan could not attach a count to. `queries: null` is a join
 * that found nothing; the zeroed shape with "pending engine run" is the same
 * thing on a scan stored before that changed (#3916).
 */
function unscored(c: AeoBusinessCompetitor): boolean {
    return c.queries === null || (c.aeo === 0 && c.queries === 0 && /pending engine run/i.test(c.note ?? ""));
}

/**
 * The scan's own line on a rival, when it says more than the count. The
 * backend's "Named on 12 of 50 questions" counts every probe, the API link
 * rows included; the row's own count, from the verdict answers, stands in its
 * place rather than beside it disagreeing.
 */
function auditNote(c: AeoBusinessCompetitor): string | null {
    const note = (c.note ?? "").trim();
    if (!note || unscored(c) || /^named on \d+/i.test(note)) return null;
    return note;
}

function buildRivals(inputs: VisibilityInputs, rows: VisRow[]): VisRival[] {
    const matches = (names: string[]) => (name: string) => names.some((n) => key(n) === key(name));
    const insight = (name: string, names: string[], seen: VisRow[], gap: string | null, win?: AeoCompetitorWin): VisRival => {
        const found = inputs.auditCompetitors.find((c) => matches(names)(c.name));
        const copy = inputs.reportCompetitors.find((c) => matches(names)(c.name));
        // The questions it takes, as the backend counted them when it sends
        // them (verdict answers only, its own name merge), else the rows' own
        // reading. Either way the rows are what a question opens.
        const instead = win?.insteadQueries ? new Set(win.insteadQueries.map(key)) : null;
        const wins = instead ? rows.filter((r) => instead.has(r.key)) : seen.filter((r) => !r.named);
        const on = instead ? [...seen, ...wins.filter((r) => !seen.includes(r))] : seen;
        return {
            name,
            domain: found?.domain.trim() || null,
            on: on.length,
            wins,
            takes: win?.insteadCount ?? wins.length,
            both: on.filter((r) => r.named && !wins.includes(r)),
            gap,
            narrative: copy?.narrative.trim() || null,
            note: found ? auditNote(found) : null,
            scored: !found || !unscored(found),
        };
    };
    const naming = (names: string[]) => (r: VisRow) => r.rivals.some(matches(names));

    const wins = inputs.vis?.competitorWins ?? [];
    const ranked = new Set(wins.flatMap((w) => [w.name, ...(w.aliases ?? [])].map(key)));
    let list: VisRival[];
    if (wins.length > 0) {
        // The backend's own list (merged names, its own ranking), joined back
        // to the rows by question text. It is tallied over every probe, the
        // API's links-only rows and withheld answers included, so a question
        // counts only where a verdict answer named the rival, and a rival no
        // verdict answer named is not listed (rule 7).
        list = wins
            .map((w) => {
                const names = [w.name, ...(w.aliases ?? [])];
                const asked = new Set(w.queries.map(key));
                return insight(w.name, names, rows.filter((r) => asked.has(r.key) && naming(names)(r)), w.gap?.text ?? null, w);
            })
            .filter((r) => r.on > 0);
    } else {
        // A run from before competitorWins: the same join, from the probes.
        const reach = new Map<string, VisRow[]>();
        rows.forEach((r) => r.rivals.forEach((name) => reach.set(name, [...(reach.get(name) ?? []), r])));
        list = [];
        reach.forEach((on, name) => list.push(insight(name, [name], on, null)));
        list = list.sort((a, b) => b.on - a.on).slice(0, RIVALS_LISTED);
    }
    // The rivals the scan found that the ranking does not hold (seeded ones
    // no answer named, or a scan that could not count them): kept, with the
    // rows that name them, so no rival the scan recorded goes missing.
    inputs.auditCompetitors
        .filter((c) => c.name.trim() && !ranked.has(key(c.name)) && !list.some((r) => key(r.name) === key(c.name)))
        .forEach((c) => list.push(insight(c.name, [c.name], rows.filter(naming([c.name])), null)));
    return list.sort((a, b) => b.takes - a.takes || b.on - a.on);
}

/**
 * Names the engines gave that the rival list does not already hold, in the
 * payload's order. The API's links-only row is not a verdict, so its names
 * are left out as the rival list leaves them out.
 */
function otherRivals(vis: AeoAiVisibility | null, listed: VisRival[]): string[] {
    const seen = new Set(listed.map((r) => key(r.name)));
    const out: string[] = [];
    (vis?.engines ?? []).filter((e) => !e.citationOnly).forEach((e) => e.rivals.forEach((name) => {
        if (!name.trim() || seen.has(key(name))) return;
        seen.add(key(name));
        out.push(name);
    }));
    return out;
}

function buildSources(inputs: VisibilityInputs, named: number): VisSources {
    const vis = inputs.vis;
    const share = vis?.citationShare ?? null;
    const measured = !!share && !share.absent && share.denominator > 0;
    const own = measured && share
        ? { count: share.count, of: share.denominator, basis: share.basis ?? null, engines: share.engines ?? [] }
        : null;
    const ownRate = own ? own.count / own.of : null;
    return {
        own,
        // A scan from before the measured share has a real count and no
        // denominator; the count is kept, no share is claimed.
        legacyOwn: share ? null : vis?.ownDomainAnswers ?? null,
        absent: share?.absent ?? null,
        capped: inputs.levelCappedBy === "rented_visibility"
            || (ownRate !== null && ownRate < RENTED_VISIBILITY_CAP_BELOW && named > 0),
        rows: (vis?.sourceDomains ?? []).map((d) => ({
            domain: d.domain,
            count: d.answers,
            own: d.own,
            kind: d.kind ?? null,
            questions: d.questions ?? null,
        })),
        total: vis?.sourceDomainsTotal ?? null,
        basis: vis?.sourceDomainsBasis ?? null,
        appShowsNoLinks: inputs.grounding?.citationsMeasurable === false,
        // `openai` here is the API pass kept for its links, not the app a
        // customer uses, and the footnote says which.
        from: (share?.engines ?? vis?.citationsFrom ?? []).map((e) => (e.toLowerCase() === "openai" ? "the OpenAI API" : engineDisplayName(e))),
    };
}

/* ------------------------------------------------------------------------- *
 * How it was measured (staff)
 * ------------------------------------------------------------------------- */

/** "visibility-set-v4.2" -> "v4.2". */
function recipeVersion(recipe: string): string {
    return recipe.match(/v\d+(?:\.\d+)*/)?.[0] ?? recipe;
}

function buildOps(inputs: VisibilityInputs, engines: Record<VisEngine, VisEngineStat>): VisOpsRow[] {
    const g = inputs.grounding;
    const cov = inputs.vis?.coverage;
    const rows: VisOpsRow[] = [];
    const asked = typeof g?.askedQuestions === "number" && typeof g.totalQuestions === "number"
        ? ` · ${g.askedQuestions} of ${g.totalQuestions} asked`
        : "";
    rows.push({ label: engines.chatgpt.label, engine: "chatgpt", value: g?.instrumentLabel ? `${g.instrumentLabel}${asked}` : "Not recorded on this scan" });
    const gem = engines.gemini;
    rows.push({ label: gem.label, engine: "gemini", value: gem.skipped ?? `API, no location · ${gem.of ?? 0} answered` });
    const gai = engines.ai_overview;
    rows.push({
        label: gai.label,
        engine: "ai_overview",
        value: gai.skipped ?? [
            gai.capturedAt ? `Read ${shortDate(gai.capturedAt)}` : "Read on this scan",
            gai.coverage ? `an overview on ${gai.coverage.answered} of ${gai.coverage.asked}` : null,
            gai.coverage?.failed ? `${gai.coverage.failed} could not be read` : null,
            gai.coverage?.pending ? `${gai.coverage.pending} still being read` : null,
        ].filter(Boolean).join(" · "),
    });
    if (cov) {
        rows.push({
            label: "Answers",
            value: `${cov.questionsAsked} asked · ${cov.questionsAnswered} answered · ${cov.answersScored} scored`,
        });
        const excluded = (cov.headlineExcluded ?? 0) + (cov.brandedExcluded ?? 0);
        if (excluded > 0) {
            rows.push({ label: "Excluded", value: `${cov.headlineExcluded ?? 0} no business could win · ${cov.brandedExcluded ?? 0} branded` });
        }
        if (cov.recipe) rows.push({ label: "Question set", value: recipeVersion(cov.recipe) });
    }
    if (inputs.levelCappedBy === "rented_visibility") {
        rows.push({ label: "Rating cap", value: "Rented visibility: the answers cite other sites. Max 3 of 5.", tone: "warn" });
    } else if (inputs.levelCappedBy === "site_health") {
        rows.push({ label: "Rating cap", value: "Site health holds the rating below what visibility alone gives.", tone: "warn" });
    }
    const scan = inputs.history.find((h) => h.assessment_id === inputs.assessmentId);
    const cost = scan?.cost?.actual_usd ?? null;
    const estimate = scan?.cost?.estimated_usd ?? null;
    rows.push({
        label: "Scan",
        value: [
            shortDate(inputs.capturedAt),
            cost !== null ? `$${cost.toFixed(2)}` : estimate !== null ? `about $${estimate.toFixed(2)}, estimated` : null,
        ].filter(Boolean).join(" · "),
    });
    return rows;
}

/* ------------------------------------------------------------------------- *
 * The model
 * ------------------------------------------------------------------------- */

export function buildVisModel(inputs: VisibilityInputs, fixTasks: AeoTrackerSummaryTask[] | null): VisModel {
    const { vis, grounding } = inputs;
    const byQuestion = verdictProbesByQuestion(inputs.probes ?? []);
    const searchBySlot = matchesBySlot(inputs.searchQuestions);
    const coverage = new Map<string, VisRow["site"]>();
    (inputs.answerCoverage?.matches ?? []).forEach((m) => {
        if (m?.status) coverage.set(key(m.query), m.status);
    });
    // A fix covers a question when the plan minted it FROM that question. The
    // summary route does not send every question a task covers yet, so other
    // rows show no fix rather than a guessed one.
    const fixFor = new Map<string, { id: string; title: string | null }>();
    (fixTasks ?? []).forEach((t) => {
        if (t.source.type === "query" && t.source.ref && !fixFor.has(key(t.source.ref))) {
            fixFor.set(key(t.source.ref), { id: t.id, title: t.action?.trim() || null });
        }
    });

    // Google's AI comes from its own block, never from the probes: the backend
    // has never written a Google row into `probes`, which is why the column was
    // empty on every question while the tile read 32% (#3945).
    const gai = googleAiByQuestion(inputs.googleAiReadings ?? []);

    // The engines THIS scan's verdict counts over, from its own rule. Read
    // once, used by every row below and by the headline.
    const countedOver = countedEngines(vis?.countingRule);

    const rows: VisRow[] = inputs.queries.map((q, i) => {
        const k = key(q.text);
        const probes = byQuestion.get(k) ?? [];
        const forEngine = (e: VisEngine) => probes.filter((p) => engineOf(p) === e);
        const id = q.n ?? q.slot_n ?? i + 1;
        const reading = googleAiFor(gai, id, k);
        // A page still being read is no cell at all: not named, not failed.
        const googlePending = reading?.pending === true;
        const cells = {
            chatgpt: cellFor(forEngine("chatgpt")),
            gemini: cellFor(forEngine("gemini")),
            // The probe path stays for scans from before the readings existed.
            ai_overview: googlePending ? null : reading ? googleAiCell(reading) : cellFor(forEngine("ai_overview")),
        };
        const excerpts: VisRow["excerpts"] = {};
        VIS_ENGINES.forEach((e) => {
            const ex = e === "ai_overview" && reading ? googleAiExcerpt(reading) : excerptFor(forEngine(e));
            if (ex) excerpts[e] = ex;
        });
        return {
            id,
            key: k,
            text: q.text,
            kind: q.check ?? null,
            searches: searchBySlot.get(id) ?? [],
            cells,
            // The engines THIS scan counted (#4025 FE-5 / FE-10, backend
            // BE-5). Leaving Google's AI out was defensible as a statement
            // about assistants and unreadable on screen: the tester found 5 of
            // 7 "Missing you" questions with Named in the Google's AI column
            // beside them. A row cannot say nobody named the business next to
            // a mark saying somebody did.
            //
            // Per scan, never a constant: an old scan keeps the stored
            // `questionsNamed` it was counted for, so counting its rows the
            // new way would put a new headline over an old table.
            named: countedOver.some((e) => cells[e]?.named),
            measured: countedOver.some((e) => cells[e] !== null),
            site: coverage.get(k) ?? null,
            rivals: uniqueNames(countedOver.flatMap((e) => cells[e]?.rivals ?? [])),
            excerpts,
            fixTaskId: fixFor.get(k)?.id ?? null,
            fixTaskTitle: fixFor.get(k)?.title ?? null,
            googlePending,
            corrected: q.corrected ?? null,
            reasking: ["clearing", "asking"].includes(inputs.questionCorrections?.[String(q.n ?? id)]?.state ?? ""),
            verdictFixes: verdictFixesOf(inputs.verdictCorrections?.[String(q.n ?? id)]),
        };
    });

    const probedEngines = new Set(Array.from(byQuestion.values()).flat().map(engineOf));
    const engines: Record<VisEngine, VisEngineStat> = {
        chatgpt: assistantStat("chatgpt", vis, rows, grounding, probedEngines.has("chatgpt")),
        gemini: assistantStat("gemini", vis, rows, grounding, probedEngines.has("gemini")),
        ai_overview: googleAiStat(vis, rows),
    };

    // Distinct questions named, from the backend, which leaves the field out
    // when no row answered. On a run from before it sent that, the best single
    // assistant stands in: it never overstates. With neither, there is no
    // count, and the headline says why rather than "0 of 50".
    const counts = countedOver.map((e) => engines[e].named).filter((n): n is number => n !== null);
    const named = vis?.questionsNamed ?? (counts.length > 0 ? Math.max(...counts) : null);
    // Out of the headline questions that got a scored answer: branded and
    // unwinnable questions leave (coverage), and so does a question whose
    // every answer was withheld or empty (rule 7).
    const probed = (inputs.probes ?? []).length > 0;
    const asked = vis?.coverage?.questionsAsked ?? rows.length;
    const excluded = (vis?.coverage?.headlineExcluded ?? 0) + (vis?.coverage?.brandedExcluded ?? 0);
    const unmeasured = probed ? rows.filter((r) => !r.measured).length : 0;
    const counted = named !== null && (!probed || rows.some((r) => r.measured));
    // A question whose Google page is still being read has no final verdict:
    // it leaves the denominator unless an assistant already named the business
    // (ourteam-backend#5619). Unmeasured rows are already out of `unmeasured`.
    const openPending = rows.filter((r) => r.googlePending && !r.named && !(probed && !r.measured)).length;
    const of = counted ? Math.max(0, (vis?.questionsOf ?? asked - excluded - unmeasured) - openPending) : 0;

    const kindCopy: VisModel["kindCopy"] = {};
    (Object.keys(vis?.checks ?? {}) as VisibilityCheckName[]).forEach((k) => {
        const c = vis?.checks?.[k];
        if (c) kindCopy[k] = { question: c.question, fix: c.fix };
    });

    const partial = isPartialInstrument(grounding);
    const rivals = buildRivals(inputs, rows);
    return {
        capturedAt: inputs.capturedAt,
        captureFor: inputs.captureFor ?? null,
        counted: countedOver,
        headline: {
            named: of > 0 ? named ?? 0 : 0,
            of,
            asked,
            verdict: vis?.verdict ?? null,
            weakest: weakestEngine(VIS_ENGINES.map((e) => engines[e])),
            benchmark: inputs.benchmark ?? null,
            absent: of > 0 ? null : absenceText(vis?.scoreAbsent ?? null),
        },
        engines,
        googlePending: googlePendingOf(inputs.googleAiPending),
        rows,
        trend: buildTrend(inputs),
        searchEmpty: { ...searchEmptyState(inputs.searchQuestions), wholeScan: !rows.some((r) => r.searches.length > 0) },
        kindCopy,
        rivals,
        otherRivals: otherRivals(vis, rivals),
        sources: buildSources(inputs, named ?? 0),
        google: {
            connected: !!inputs.searchPerformance?.connected,
            avgPosition: inputs.searchPerformance?.totals?.avgPosition ?? null,
            top10: inputs.searchPerformance?.buckets?.top10 ?? null,
            clicks: inputs.searchPerformance?.totals?.clicks ?? null,
            days: inputs.searchPerformance?.window?.days ?? null,
            // The searches that bring visits, most visits first.
            keywords: (inputs.searchPerformance?.keywords ?? [])
                .filter((k) => k.clicks > 0)
                .sort((a, b) => b.clicks - a.clicks)
                .slice(0, 5)
                .map((k) => ({ query: k.query, clicks: k.clicks, position: k.position })),
        },
        ops: buildOps(inputs, engines),
        instrumentWarning: grounding?.instrumentNote?.trim()
            || (partial
                ? "Only some questions were asked in the ChatGPT app; the rest are OpenAI API answers, which read lower than what a customer sees."
                : null),
        chatgptInstrument: grounding?.instrumentLabel
            ? [grounding.instrumentLabel, grounding.instrumentNote].filter(Boolean).join(" · ")
            : null,
        hasSite: rows.some((r) => r.site !== null),
        hasSamples: rows.some((r) => VIS_ENGINES.some((e) => (r.cells[e]?.samples ?? 0) > 1)),
    };
}

/** The scan a workspace screen reads: the latest, or the past one opened. */
export type LatestAssessment = NonNullable<AeoBusinessDetail["latest_assessment"]>;

/**
 * The inputs for a live business, read off the scan on screen. The history
 * and the capture date come from the workspace, which already holds them.
 */
export function visibilityInputsFromAssessment(
    la: LatestAssessment,
    extra: { capturedAt: string; history: AeoScoreHistoryEntry[] },
): VisibilityInputs {
    const r = la.results;
    return {
        capturedAt: extra.capturedAt,
        assessmentId: la.assessment_id ?? null,
        vis: r?.aiVisibility ?? null,
        queries: r?.queries ?? [],
        questionCorrections: r?.questionCorrections,
        verdictCorrections: r?.verdictCorrections,
        probes: r?.probes,
        googleAiReadings: r?.googleAiReadings,
        googleAiPending: r?.googleAiPending ?? null,
        grounding: r?.grounding ?? null,
        answerCoverage: r?.answerCoverage ?? null,
        searchQuestions: r?.searchQuestions ?? null,
        searchPerformance: r?.searchPerformance ?? null,
        trendBreak: r?.queries_trend_break?.at
            ? { at: r.queries_trend_break.at, reason: r.queries_trend_break.reason || "the question set changed here" }
            : null,
        auditCompetitors: r?.businessAudit?.competitors ?? [],
        reportCompetitors: (r?.report_copy?.competitors ?? []).filter((c) => !!c?.name?.trim() && !!c.narrative?.trim()),
        history: extra.history,
        levelCappedBy: r?.reportMetrics?.levelCappedBy ?? null,
    };
}

/** The route's engine names onto the screen's: Google's AI is `google_ai` there, `ai_overview` here. */
function verdictFixesOf(raw: Partial<Record<AeoVerdictEngine, AeoVerdictCorrection>> | undefined): Partial<Record<VisEngine, AeoVerdictCorrection>> | undefined {
    if (!raw) return undefined;
    const out: Partial<Record<VisEngine, AeoVerdictCorrection>> = {};
    if (raw.chatgpt) out.chatgpt = raw.chatgpt;
    if (raw.gemini) out.gemini = raw.gemini;
    if (raw.google_ai) out.ai_overview = raw.google_ai;
    return Object.keys(out).length ? out : undefined;
}
